import React from 'react';
import Box from '@mui/material/Box';
import useStyles from './styles';
import Typography from '@mui/material/Typography';
import { ROUTES } from 'config/Nav';
import { Skeleton } from '@mui/material';
import { BaseLink, ImageProps } from '@elevator/ui';
import Image from 'next/image';

/**
 * Hero Component
 *
 * Displays a hero banner at the top of the home page with a large background image,
 * gradient overlay, and title. Features a responsive layout (90vh height on mobile,
 * 85vh on large screens) and loading skeleton state.
 *
 * The component uses Next.js Image optimization with priority loading for above-the-fold
 * performance. The gradient overlay provides text contrast for the title displayed
 * at the bottom of the hero image.
 *
 * @param props - Component props
 * @param props.postTitle - The title of the featured post
 * @param props.postUrl - The URL path to the post (used for navigation)
 * @param props.isLoading - Whether the post data is still loading (shows skeleton)
 * @param props.sizes - Responsive image sizes attribute (defaults to "100vw")
 * @param props.src - Image source URL
 * @param props.alt - Alt text for the hero image
 */
const Hero = (props: HeroPropTypes) => {
  const { classes } = useStyles();

  const { src, alt, postTitle, postUrl, isLoading, sizes = '100vw' } = props;

  // Error handling: Don't render if image source is missing
  if (!src && !isLoading) {
    return null;
  }

  return (
    <BaseLink
      href={ROUTES.POST.fileApiPath}
      as={postUrl}
      aria-label={`View post: ${postTitle}`}
      data-component="Hero"
    >
      <Box className={classes.root}>
        {isLoading ? (
          <Skeleton
            variant="rectangular"
            width="100%"
            height="100%"
            animation="pulse"
            className={classes.skeleton}
          >
            <Box className={classes.heroImage} />
          </Skeleton>
        ) : (
          <Image
            src={src}
            className={classes.heroImage}
            alt={alt}
            fill
            sizes={sizes}
            priority
          />
        )}
        <Box className={classes.gradientContainer}>
          <Typography variant="h2" className={classes.postTitle} align="center">
            {postTitle}
          </Typography>
        </Box>
      </Box>
    </BaseLink>
  );
};

export interface HeroPropTypes extends Omit<ImageProps, 'srcSet'> {
  /** The title of the featured post */
  postTitle: string;
  /** The URL path to the post (used for navigation) */
  postUrl: string;
  /** Whether the post data is still loading (shows skeleton) */
  isLoading: boolean;
  /** Responsive image sizes attribute (defaults to "100vw") */
  sizes?: string;
}

export default Hero;
